<template>
  <div class="chat">
    <div ref="scroll" class="scroll">
      <div class="inner">
        <div v-if="loading" class="state">
          <b-spinner small/>
          <span class="ml-2">Загрузка истории...</span>
        </div>

        <div v-else-if="!sessionId" class="empty">
          <h2>Создайте новый чат</h2>
          <p>или выберите диалог в боковой панели слева.</p>
        </div>

        <div v-else-if="!messages.length && !streaming" class="empty">
          <h2>Задайте вопрос о ваших данных</h2>
          <p>Например: «Сколько новых команд за этот месяц?»</p>
        </div>

        <template v-for="m in messages">
          <UserMessage v-if="m.kind === 'user'" :key="m.localId" :text="m.text"/>
          <AssistantMessage v-else :key="m.localId" :message="m"/>
        </template>
      </div>
    </div>

    <div class="composer-wrap">
      <div class="composer">
        <textarea
            ref="textarea"
            v-model="input"
            class="composer-input"
            rows="1"
            :placeholder="placeholderText"
            :disabled="!sessionId || streaming"
            @keydown.enter.exact.prevent="onSubmit"
            @input="autoResize"
        />
        <button
            v-if="streaming"
            class="action-btn stop"
            title="Остановить"
            @click="onStop"
        >
          <feather type="square" size="16"/>
        </button>
        <button
            v-else
            class="action-btn send"
            :disabled="!sessionId || !input.trim()"
            title="Отправить"
            @click="onSubmit"
        >
          <feather type="arrow-up" size="16"/>
        </button>
      </div>
      <div class="hint">Enter — отправить, Shift+Enter — перенос строки</div>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';
import { askAi } from '@/services/aiChatStream';
import UserMessage from './UserMessage.vue';
import AssistantMessage from './AssistantMessage.vue';

let LOCAL_ID = 0;
const nextId = () => `m_${++LOCAL_ID}`;

function buildAssistant(extra = {}) {
  return {
    kind: 'assistant',
    localId: nextId(),
    thinking: false,
    summary: '',
    methodology: '',
    sql_used: '',
    chart: null,
    steps: [],
    error: false,
    errorMessage: '',
    aborted: false,
    ...extra,
  };
}

function normalizeHistory(rawMessages) {
  const result = [];
  let pendingSteps = [];
  const stepsById = new Map();

  (rawMessages || []).forEach(msg => {
    const blocks = Array.isArray(msg.content) ? msg.content : [];
    blocks.forEach(b => {
      if (msg.role === 'user' && b.type === 'text') {
        result.push({ kind: 'user', localId: nextId(), text: b.text || '' });
      } else if (msg.role === 'user' && b.type === 'tool_result') {
        const step = stepsById.get(b.tool_use_id);
        if (step) {
          step.finished = true;
          if (b.is_error) {
            step.is_error = true;
            step.error_message = typeof b.content === 'string' ? b.content : (b.message || 'Ошибка');
          } else {
            const text = typeof b.content === 'string' ? b.content : '';
            const m = text.match(/total_rows:\s*(\d+)/);
            if (m) step.total_rows = Number(m[1]);
          }
        }
      } else if (msg.role === 'assistant' && b.type === 'tool_use') {
        if (b.name === 'execute_sql') {
          const step = {
            id: b.id,
            query: (b.input && b.input.query) || '',
            finished: false,
            is_error: false,
            error_message: '',
            elapsed_ms: null,
            columns: [],
            rows: [],
            total_rows: 0,
          };
          pendingSteps.push(step);
          stepsById.set(b.id, step);
        } else if (b.name === 'final_answer') {
          const inp = b.input || {};
          const assistant = buildAssistant({
            summary: inp.summary || '',
            methodology: inp.methodology || '',
            sql_used: inp.sql_used || '',
            chart: inp.chart || null,
            steps: pendingSteps,
          });
          result.push(assistant);
          pendingSteps = [];
        }
      } else if (msg.role === 'assistant' && b.type === 'text') {
        const last = result[result.length - 1];
        if (last && last.kind === 'assistant' && !last.summary) {
          last.summary = b.text || '';
        } else {
          result.push(buildAssistant({
            summary: b.text || '',
            steps: pendingSteps,
          }));
          pendingSteps = [];
        }
      }
    });
  });
  if (pendingSteps.length) {
    result.push(buildAssistant({ steps: pendingSteps, aborted: true }));
  }
  return result;
}

export default {
  name: 'AiChatWindow',
  components: { UserMessage, AssistantMessage },
  props: {
    sessionId: { type: [Number, String], default: null },
  },
  data() {
    return {
      messages: [],
      input: '',
      loading: false,
      streaming: false,
      controller: null,
      currentAssistant: null,
      pendingSteps: [],
    };
  },
  computed: {
    placeholderText() {
      if (!this.sessionId) return 'Сначала создайте чат';
      if (this.streaming) return 'ИИ отвечает...';
      return 'Спросите ИИ...';
    },
  },
  watch: {
    sessionId: {
      immediate: true,
      handler(id) {
        this.cancelStream();
        this.messages = [];
        if (id != null) this.loadHistory(id);
      },
    },
  },
  beforeDestroy() {
    this.cancelStream();
  },
  methods: {
    async loadHistory(id) {
      this.loading = true;
      try {
        const resp = await api.aiChat.getMessages(id);
        const raw = (resp && resp.data && resp.data.data) || [];
        this.messages = normalizeHistory(raw);
        this.$nextTick(() => this.scrollToBottom(true));
      } catch (e) {
        void e;
      } finally {
        this.loading = false;
      }
    },

    cancelStream() {
      if (this.controller) {
        try { this.controller.cancel(); } catch (e) { void e; }
      }
      this.controller = null;
      this.streaming = false;
      this.currentAssistant = null;
      this.pendingSteps = [];
    },

    autoResize(e) {
      const el = e && e.target ? e.target : this.$refs.textarea;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    },

    onSubmit() {
      const text = this.input.trim();
      if (!text || !this.sessionId || this.streaming) return;
      this.input = '';
      this.$nextTick(() => {
        const el = this.$refs.textarea;
        if (el) el.style.height = 'auto';
      });
      this.sendQuestion(text);
    },

    onStop() {
      if (this.controller) this.controller.cancel();
      if (this.currentAssistant) {
        this.currentAssistant.aborted = true;
        this.currentAssistant.thinking = false;
      }
      this.streaming = false;
      this.controller = null;
      this.currentAssistant = null;
      this.$emit('stream-end');
    },

    sendQuestion(question) {
      this.messages.push({ kind: 'user', localId: nextId(), text: question });
      const assistant = buildAssistant({ thinking: true });
      this.currentAssistant = assistant;
      this.pendingSteps = [];
      this.messages.push(assistant);
      this.streaming = true;
      this.$emit('stream-start', { sessionId: this.sessionId });
      this.$nextTick(() => this.scrollToBottom(true));

      this.controller = askAi({
        sessionId: this.sessionId,
        question,
        onEvent: (e) => this.handleEvent(e),
      });
    },

    handleEvent({ event, data }) {
      const a = this.currentAssistant;
      if (!a) return;

      if (event === 'thinking') {
        a.thinking = true;
      } else if (event === 'tool_use') {
        a.thinking = false;
        if (data && data.name === 'execute_sql') {
          const step = {
            id: data.id,
            query: (data.input && data.input.query) || '',
            finished: false,
            is_error: false,
            error_message: '',
            elapsed_ms: null,
            columns: [],
            rows: [],
            total_rows: 0,
          };
          this.pendingSteps.push(step);
          a.steps = [...a.steps, step];
        }
      } else if (event === 'tool_result') {
        const step = a.steps.find(s => s.id === data.tool_use_id);
        if (step) {
          step.finished = true;
          step.is_error = !!data.is_error;
          step.error_message = data.message || '';
          step.elapsed_ms = data.elapsed_ms ?? null;
          step.columns = data.columns || [];
          step.rows = data.rows || [];
          step.total_rows = data.total_rows ?? (data.rows ? data.rows.length : 0);
        }
      } else if (event === 'text_delta') {
        a.thinking = false;
        a.summary = (a.summary || '') + (data && data.text ? data.text : '');
      } else if (event === 'final') {
        a.thinking = false;
        a.summary = data.summary || a.summary || '';
        a.methodology = data.methodology || '';
        a.sql_used = data.sql_used || '';
        a.chart = data.chart || null;
      } else if (event === 'error') {
        a.thinking = false;
        a.error = true;
        a.errorMessage = (data && data.message) || 'Неизвестная ошибка';
      } else if (event === 'aborted') {
        a.thinking = false;
        a.aborted = true;
        this.streaming = false;
        this.controller = null;
        this.$emit('stream-end');
      } else if (event === 'done') {
        a.thinking = false;
        this.streaming = false;
        this.controller = null;
        this.$emit('stream-end');
      }

      this.$nextTick(this.scrollToBottom);
    },

    scrollToBottom(force = false) {
      const el = this.$refs.scroll;
      if (!el) return;
      const threshold = 120;
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (force || distance < threshold) {
        el.scrollTop = el.scrollHeight;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--ai-bg);
}

.scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}
.inner {
  max-width: 760px;
  margin: 0 auto;
  padding: 28px 24px 12px;
}

.state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--ai-text-muted);
}

.empty {
  text-align: center;
  padding: 80px 20px 40px;
  color: var(--ai-text);

  h2 {
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0 0 6px;
  }
  p {
    color: var(--ai-text-muted);
    font-size: 15px;
    margin: 0;
  }
}

.composer-wrap {
  flex-shrink: 0;
  padding: 8px 24px 18px;
  max-width: 760px;
  margin: 0 auto;
  width: 100%;
}

.composer {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: var(--ai-bg);
  border: 1px solid var(--ai-border);
  border-radius: var(--ai-radius-pill);
  padding: 6px 6px 6px 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: border-color .15s ease, box-shadow .15s ease;

  &:focus-within {
    border-color: #c5c5d2;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }
}

.composer-input {
  flex: 1;
  min-height: 36px;
  max-height: 200px;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: var(--ai-font);
  font-size: 15px;
  line-height: 1.5;
  color: var(--ai-text);
  padding: 8px 0;

  &::placeholder { color: var(--ai-text-subtle); }
  &:disabled { opacity: .65; }
}

.action-btn {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color .12s ease, color .12s ease, opacity .12s ease;

  &.send {
    background: var(--ai-accent);
    color: var(--ai-accent-fg);

    &:hover:not(:disabled) { opacity: .85; }
    &:disabled { background: #d9d9e3; color: #fff; cursor: default; }
  }
  &.stop {
    background: var(--ai-text);
    color: var(--ai-bg);
    &:hover { opacity: .85; }
  }
}

.hint {
  text-align: center;
  font-size: 11.5px;
  color: var(--ai-text-subtle);
  margin-top: 8px;
}
</style>
